import {
  Box,
  Button,
  ButtonGroup,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useColorMode,
  useMediaQuery,
} from "@chakra-ui/react";
import { ReactElement, ReactNode } from "react";
import LoadingAnimation from "../Loader/LoadingAnimation";
import { NextImage } from "../NextImage/NextImage";

export interface DialogProps {
  loading?: boolean;
  icon?: any;
  iconNode?: ReactElement | null;
  title?: any;
  children?: ReactNode;
  isOpen?: boolean;
  isClose?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
  actions?: {
    text: string;
    onClick: () => void;
    primary?: boolean;
  }[];
  size?: string;
  bodyPadding?: any;
  height?: any;
  maxHeight?: any;
  isError?: boolean;
  width?: any;
  isFooter?: boolean;
  isSuccess?: boolean;
  isCloseDrawer?: boolean;
}

const Dialog = ({
  loading,
  icon: icon = "",
  iconNode,
  title: title = "",
  children,
  isOpen: isOpen = false,
  isClose,
  onOpen,
  onClose: onClose = () => {},
  actions: actions = [],
  size: size = "md",
  bodyPadding: bodyPadding = null,
  height,
  maxHeight,
  isError: isError = false,
  width,
  isFooter: isFooter = true,
  isSuccess: isSuccess = false,
  isCloseDrawer: isCloseDrawer = false,
}: DialogProps) => {
  const { colorMode } = useColorMode();
  const [isMobile] = useMediaQuery("(max-width: 768px)");
  size = isMobile ? "md" : size || "md";
  isClose = loading ? false : isClose ?? true;

  const DialogIcon = () => {
    if (loading) return <LoadingAnimation />;
    return !!iconNode ? (
      iconNode
    ) : icon ? (
      <NextImage className="w-6 svg-primary" src={icon} />
    ) : (
      <></>
    );
  };

  if (isMobile) {
    return (
      <Drawer
        placement="bottom"
        onClose={loading ? () => {} : onClose}
        isOpen={isOpen}
        blockScrollOnMount={false}
      >
        <DrawerOverlay />
        <DrawerContent maxHeight={maxHeight} minHeight={height}>
          {!loading && isCloseDrawer && <DrawerCloseButton />}
          {!loading && (
            <DrawerHeader>
              {isError ? (
                <Box as="h2" fontSize="24px" className="auto-center">
                  <Box as="div" marginBottom="16px">
                    <DialogIcon />
                  </Box>
                  <Text color="#EB5757" fontSize="20px" lineHeight="25px">
                    {title}
                  </Text>
                </Box>
              ) : (
                <Box
                  as="h2"
                  className="text-center items-center flex justify-center gap-2"
                  fontSize="24px"
                  color={
                    isSuccess
                      ? colorMode === "dark"
                        ? "#45BFB0"
                        : "#00A28D"
                      : colorMode === "dark"
                      ? "#FFF"
                      : "#23262F"
                  }
                >
                  <DialogIcon />
                  {title}
                </Box>
              )}
            </DrawerHeader>
          )}
          <DrawerBody padding={bodyPadding}>
            {loading ? (
              <div className="m-auto">
                <LoadingAnimation />
              </div>
            ) : (
              children
            )}
          </DrawerBody>
          {!loading && (
            <DrawerFooter>
              {actions && actions.length > 0 ? (
                <ButtonGroup variant="ghost" className="self-end" spacing="4">
                  {actions.map((action, i) => {
                    return (
                      <Button
                        textTransform="capitalize"
                        color={action.primary ? "primary.300" : "neutral.200"}
                        onClick={action.onClick}
                        key={i}
                        fontSize="0.9em"
                      >
                        {action.text}
                      </Button>
                    );
                  })}
                </ButtonGroup>
              ) : (
                <></>
              )}
            </DrawerFooter>
          )}
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Modal
      onClose={loading ? () => {} : onClose}
      isOpen={isOpen}
      isCentered
      size={size}
      blockScrollOnMount={false}
    >
      <ModalOverlay />
      <ModalContent minHeight={height} maxWidth={width}>
        {!loading && (
          <ModalHeader>
            {isError ? (
              <Box as="h2" fontSize="24px" className="auto-center">
                <Box as="div" marginBottom="16px">
                  <DialogIcon />
                </Box>
                <Text
                  style={{ color: "#EB5757" }}
                  fontSize="20px"
                  lineHeight="25px"
                >
                  {title}
                </Text>
              </Box>
            ) : (
              <Box
                as="h2"
                className="text-center pt-4 items-center flex justify-center  gap-2"
                fontSize="24px"
              >
                <DialogIcon />
                <Text
                  color={
                    isSuccess
                      ? colorMode === "dark"
                        ? "#45BFB0"
                        : "#00A28D"
                      : colorMode === "dark"
                      ? "#FFF"
                      : "#23262F"
                  }
                  fontSize="20px"
                  lineHeight="25px"
                >
                  {title}
                </Text>
              </Box>
            )}
          </ModalHeader>
        )}
        {isClose && !loading && <ModalCloseButton />}
        <ModalBody padding={bodyPadding}>
          {loading ? (
            <div className="m-auto">
              <LoadingAnimation />
            </div>
          ) : (
            children
          )}
        </ModalBody>
        {isFooter && !loading && (
          <ModalFooter>
            {actions && actions.length > 0 ? (
              <ButtonGroup variant="ghost" className="self-end" spacing="4">
                {actions.map((action, i) => {
                  return (
                    <Button
                      textTransform="capitalize"
                      color={action.primary ? "primary.300" : "neutral.200"}
                      onClick={action.onClick}
                      key={i}
                      fontSize="0.9em"
                    >
                      {action.text}
                    </Button>
                  );
                })}
              </ButtonGroup>
            ) : (
              <></>
            )}
          </ModalFooter>
        )}
      </ModalContent>
    </Modal>
  );
};
export default Dialog;
